import * as React from 'react'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Sheet, SheetBody, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { Tooltip } from '@/components/ui/tooltip'

interface RecordSheetProps<Id extends string> {
  /** What one row is, as the tray's title: "API request", "Webhook delivery", "Email". */
  title: string
  /** The ids of the rows on screen, in table order; Previous and Next step through them. */
  ids: readonly Id[]
  /** The record open in the tray (from the URL), or null when it's closed. */
  selectedId: Id | null
  /**
   * Opens another record, or closes the tray with null. Stepping asks to
   * replace the history entry, so Back closes the tray rather than retracing
   * every record stepped through.
   */
  onSelect: (id: Id | null, options: { replace: boolean }) => void
  /** The record's details, for the id on screen. */
  children: (id: Id) => React.ReactNode
}

/** Keys typed into a field are text, not navigation. */
function isTyping(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLElement &&
    (target.isContentEditable || target instanceof HTMLInputElement || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement)
  )
}

/** The table row for a record (rows carry `data-record-id`), so focus can go back to it. */
function rowButton(id: string): HTMLElement | null {
  for (const element of document.querySelectorAll('[data-record-id]')) {
    if (!(element instanceof HTMLElement) || element.dataset['recordId'] !== id) continue
    const control = element.querySelector('button, a')
    return control instanceof HTMLElement ? control : null
  }
  return null
}

/**
 * The record tray (design-system/COMPONENTS.md#list-and-detail-recordsheet):
 * a row's details slide in from the right over the dimmed list. Previous and
 * Next (↑/↓ or K/J) step through the rows on screen without closing it;
 * Escape, the close button, or the scrim closes it, and focus returns to the
 * row last shown.
 */
export function RecordSheet<Id extends string>({ title, ids, selectedId, onSelect, children }: RecordSheetProps<Id>) {
  // Keep showing the last record while the tray slides out after closing.
  const [shownId, setShownId] = React.useState(selectedId)
  if (selectedId !== null && selectedId !== shownId) setShownId(selectedId)
  const contentRef = React.useRef<HTMLDivElement>(null)

  // The record last asked for. The URL can lag a fast key repeat by a render,
  // so stepping counts from here rather than from what's on screen.
  const latestIdRef = React.useRef(selectedId)
  React.useEffect(() => {
    latestIdRef.current = selectedId
  }, [selectedId])

  const index = shownId === null ? -1 : ids.indexOf(shownId)
  const open = selectedId !== null

  const step = React.useCallback(
    (offset: 1 | -1) => {
      const at = latestIdRef.current === null ? -1 : ids.indexOf(latestIdRef.current)
      const target = at === -1 ? undefined : ids[at + offset]
      if (target === undefined) return
      latestIdRef.current = target
      onSelect(target, { replace: true })
    },
    [ids, onSelect],
  )

  React.useEffect(() => {
    if (!open) return undefined
    function onKeyDown(event: KeyboardEvent): void {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey || event.shiftKey || isTyping(event.target)) return
      if (event.key === 'ArrowUp' || event.key === 'k') step(-1)
      else if (event.key === 'ArrowDown' || event.key === 'j') step(1)
      else return
      event.preventDefault()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, step])

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) onSelect(null, { replace: false })
      }}
    >
      <SheetContent
        ref={contentRef}
        side="right"
        data-density="compact"
        {...(index === -1 ? { 'aria-describedby': undefined } : {})}
        // Focus the panel itself, not Previous: a focused button would pop its tooltip on every open.
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          contentRef.current?.focus()
        }}
        onCloseAutoFocus={(event) => {
          const row = shownId === null ? null : rowButton(shownId)
          if (row) {
            event.preventDefault()
            row.focus()
          }
        }}
      >
        <SheetHeader>
          <div className="min-w-0 flex-1">
            <SheetTitle>{title}</SheetTitle>
            {index === -1 ? null : (
              <SheetDescription>
                {index + 1} of {ids.length}
              </SheetDescription>
            )}
          </div>
          <Tooltip content="Previous (↑ or K)">
            <Button variant="ghost" size="icon-sm" aria-label="Previous" disabled={index <= 0} onClick={() => step(-1)}>
              <ChevronUp />
            </Button>
          </Tooltip>
          <Tooltip content="Next (↓ or J)">
            <Button variant="ghost" size="icon-sm" aria-label="Next" disabled={index === -1 || index === ids.length - 1} onClick={() => step(1)}>
              <ChevronDown />
            </Button>
          </Tooltip>
        </SheetHeader>
        {/* Keyed by record, so stepping starts each one scrolled to the top. */}
        <SheetBody key={shownId}>{shownId === null ? null : children(shownId)}</SheetBody>
      </SheetContent>
    </Sheet>
  )
}

interface RecordHeadlineProps {
  /** The record's name, its status badge, and an optional action (`ml-auto`) on one row. */
  children: React.ReactNode
  /** A `RecordFacts` list under that row. */
  facts?: React.ReactNode
}

/** The tray body's opening block: the record's name, its status, and an optional action on the right. */
export function RecordHeadline({ children, facts }: RecordHeadlineProps) {
  return (
    <div className="space-y-3 border-b border-border p-card">
      <div className="flex flex-wrap items-center gap-2">{children}</div>
      {facts}
    </div>
  )
}

/** A labelled fact list inside the tray (`7rem` caption labels): alternate `dt` and `dd`. */
export function RecordFacts({ children }: React.PropsWithChildren) {
  return <dl className="grid grid-cols-[7rem_minmax(0,1fr)] gap-x-3 gap-y-1 text-caption">{children}</dl>
}

interface RecordSectionProps {
  title: string
  children: React.ReactNode
}

/** A titled section of the tray body, divided from the next by a hairline. */
export function RecordSection({ title, children }: RecordSectionProps) {
  return (
    <section className="space-y-1 border-b border-border p-card last:border-b-0">
      <h3 className="text-caption font-medium text-muted-foreground">{title}</h3>
      {children}
    </section>
  )
}
